"use client";

import {
	VIDEO_HERO_IMAGES_EVENT,
	VIDEO_HERO_TEMPLATE_EVENT,
} from "@marketing/home/components/HeroControls";
import type {
	HeroPreset,
	HeroPresetQuality,
	HeroPresetTemplate,
} from "@marketing/home/components/VideoGeneratorHero";
import { useImageToVideo } from "@saas/image-to-video/hooks/use-image-to-video";
import type { I2VAspectRatio } from "@saas/image-to-video/types";
import {
	getBillableVideoSeconds,
	getI2VCreditCost,
	getI2VModel,
	getI2VPromptMaxLength,
	supportsI2VReferenceVideo,
} from "@saas/image-to-video/types";
import type { HistoryFilter } from "@saas/shared/history/types";
import { useEffect, useMemo, useRef, useState } from "react";

function getTemplatePrompt(
	template: HeroPresetTemplate,
	qualityId: string | undefined,
	optionalEmpty = false,
): string {
	const variant = (optionalEmpty && template.optionalEmpty) || template;
	return (
		(qualityId && variant.promptByQuality?.[qualityId]) || variant.prompt
	);
}

/** 模板在这一档固定带的参考视频：按档换的优先（Ozil Goal 的 Lite 只挂 2 秒），没有就用模板的那段 */
function getTemplateVideo(
	template: HeroPresetTemplate | undefined,
	qualityId: string | undefined,
	optionalEmpty = false,
) {
	const variant = (optionalEmpty && template?.optionalEmpty) || template;
	return (
		(qualityId && variant?.referenceVideoByQuality?.[qualityId]) ||
		variant?.referenceVideo
	);
}

/**
 * Studio 特效页（2026-10-23 先挪 Hotel Lobby 试点）的表单逻辑：锁 reference 模式 + 固定槽位，
 * 模型 / 分辨率收成几档画质，提示词 = 模板 + 一句 director's note。
 * 与 HeroPreset 的模板、选填槽位和参考视频配置保持兼容。
 */
export function usePresetVideoForm(
	preset: HeroPreset,
	historyFilter: HistoryFilter = "all",
) {
	const defaultQuality =
		preset.qualities.find((item) => item.id === preset.defaultQualityId) ??
		preset.qualities[0];
	// 比例在本地记一份：Grok 要两张图齐了才让挑比例（aspectRatioMinImages），
	// 没齐时 hook 会把它改成 auto，图齐了再按本地这份写回去
	const [presetRatio, setPresetRatio] = useState<I2VAspectRatio>(
		preset.defaultAspectRatio,
	);
	// 右栏 History 同 /image-to-video：全部 / 图片 / 视频，默认全部
	const form = useImageToVideo(historyFilter, {
		initial: {
			mode: preset.mode,
			model: defaultQuality.model,
			resolution: defaultQuality.resolution,
			aspectRatio: preset.defaultAspectRatio,
			duration: defaultQuality.duration ?? preset.defaultDuration,
			prompt:
				preset.templates[0] &&
				getTemplatePrompt(
					preset.templates[0],
					defaultQuality.id,
					preset.requiredSlots !== undefined,
				),
		},
		referenceSlots: preset.slotLabels.length,
		requiredReferenceSlots: preset.requiredSlots,
		referenceVideoImageSlots: preset.referenceVideoImageSlots,
		pendingStateKey: preset.pendingStateKey,
	});
	const {
		mode,
		setMode,
		model,
		setModel,
		resolution,
		setResolution,
		lockedResolutions,
		setPaywallReason,
		aspectRatio,
		canPickAspectRatio,
		setAspectRatio,
		duration,
		setDuration,
		prompt,
		setPrompt,
		videoLimits,
		referenceMedia,
		restoreReferenceMedia,
		addReferenceMedia,
		inputImages,
		setImageAt,
	} = form;

	// hook 的 prompt 就是「模板 + note」拼好的结果，模板和 note 每次从它反推，
	// 登录回来恢复现场时两者自然对上，不另存 state。模板可能按画质档换了写法，每种写法都要认得出来
	const activeMatch = preset.templates
		.flatMap((item) =>
			[item, ...(item.optionalEmpty ? [item.optionalEmpty] : [])]
				.flatMap((variant) => [
					variant.prompt,
					...Object.values(variant.promptByQuality ?? {}),
				])
				.map((text) => ({ template: item, text })),
		)
		.find(
			({ text }) =>
				prompt === text || prompt.startsWith(text.concat("\n")),
		);
	const activeTemplate = activeMatch?.template;
	const note = activeMatch
		? prompt.slice(activeMatch.text.length + preset.notePrefixLength)
		: "";
	// 认档先看「模型 + 分辨率 + 固定时长」：Ozil Goal 的 Lite 和 Standard 都是 Mini 480p，只差 4 / 15 秒。
	// 对不上固定时长的（历史行回填了别的秒数）再按模型 + 分辨率认第一档，下面的锁会把时长改回去
	const sameModel = (item: HeroPresetQuality) =>
		item.model === model && item.resolution === resolution;
	const activeQuality =
		preset.qualities.find(
			(item) => sameModel(item) && item.duration === duration,
		) ?? preset.qualities.find(sameModel);
	const optionalEmpty =
		preset.requiredSlots !== undefined &&
		inputImages.slice(preset.requiredSlots).every((image) => !image);
	// 当前画质档该用的那套模板写法；和 activeMatch.text 不一致时同步 effect 会换过去
	const activeTemplatePrompt = activeTemplate
		? getTemplatePrompt(activeTemplate, activeQuality?.id, optionalEmpty)
		: undefined;
	// 模板固定带的参考视频：这一档的模型收视频才带（videoLimits 只在收的时候有值）
	const presetVideo = videoLimits
		? getTemplateVideo(activeTemplate, activeQuality?.id, optionalEmpty)
		: undefined;
	const lockedRatio = presetVideo?.aspectRatio;
	// 用户自己传视频的模板（AI Dance 的 Your own dance）：没有固定参考视频，只开 qualityIds 里的档
	const userVideo = activeTemplate?.userVideo;
	const isQualityDisabled = (quality: HeroPresetQuality) =>
		Boolean(userVideo && !userVideo.qualityIds.includes(quality.id));
	// 各模板固定带的参考视频地址：切到用户视频模板时只清这些，用户自己传的留着
	const presetVideoUrls = useMemo(
		() =>
			new Set(
				preset.templates.flatMap((item) =>
					[item, ...(item.optionalEmpty ? [item.optionalEmpty] : [])]
						.flatMap((variant) => [
							variant.referenceVideo,
							...Object.values(
								variant.referenceVideoByQuality ?? {},
							),
						])
						.flatMap((video) => (video ? [video.url] : [])),
				),
			),
		[preset.templates],
	);
	const userVideos = referenceMedia.filter(
		(item) => item.kind === "video" && !presetVideoUrls.has(item.url ?? ""),
	);
	const userVideoMissing = Boolean(
		userVideo && !userVideos.some((item) => item.url && item.durationSec),
	);
	const addUserVideo = (file: File) => {
		if (userVideo) {
			// 传上来后还能随便切档，最短时长按开放的几档里最严的卡
			const minSeconds = Math.max(
				...preset.qualities
					.filter((item) => userVideo.qualityIds.includes(item.id))
					.map(
						(item) =>
							getI2VModel(item.model).referenceVideo
								?.minSeconds ?? 0,
					),
			);
			addReferenceMedia(
				[{ file, kind: "video" }],
				[],
				userVideo.maxSeconds,
				minSeconds,
			);
		}
	};
	// 时长分段：这一档固定了时长就只剩它一个
	const presetDurations = activeQuality?.duration
		? [activeQuality.duration]
		: preset.durationOptions;

	const selectQuality = (quality: HeroPresetQuality) => {
		if (isQualityDisabled(quality)) {
			return;
		}
		if (lockedResolutions.has(quality.resolution)) {
			setPaywallReason("video-resolution");
			return;
		}
		setModel(quality.model);
		setResolution(quality.resolution);
		// 同模型同分辨率的两档只差时长（Ozil Goal Lite / Standard），不顺手设时长就切不过去
		if (quality.duration) {
			setDuration(quality.duration);
		}
		// setModel 会把没齐图时的 auto 夹成新模型的第一个合法值，再被下面的同步 effect 反写进
		// presetRatio。这里按本地记的比例写回，同一批更新里后写的赢
		setAspectRatio(presetRatio);
	};
	const selectRatio = (ratio: I2VAspectRatio) => {
		setPresetRatio(ratio);
		if (canPickAspectRatio) {
			setAspectRatio(ratio);
		}
	};
	const setNote = (value: string) =>
		setPrompt(
			preset.buildPrompt(
				activeTemplatePrompt ?? activeTemplate?.prompt ?? "",
				value,
			),
		);
	const selectTemplate = (template: HeroPresetTemplate) =>
		setPrompt(
			preset.buildPrompt(
				getTemplatePrompt(template, activeQuality?.id, optionalEmpty),
				note,
			),
		);

	/** 每档标价：没固定时长的档按切过去后的实际时长算，模板带固定参考视频时收视频的档加这一档那段的秒数 */
	const qualityCredits = (quality: HeroPresetQuality) => {
		// 用户视频模板：传了按实际秒数，没传按默认时长那么长的一段估
		const video = userVideo
			? {
					durationSec:
						userVideos.find((item) => item.durationSec)
							?.durationSec ?? preset.defaultDuration,
				}
			: getTemplateVideo(activeTemplate, quality.id, optionalEmpty);
		return getI2VCreditCost(
			quality.model,
			quality.resolution,
			quality.duration ??
				(preset.durationOptions.includes(duration)
					? duration
					: preset.defaultDuration),
			video && supportsI2VReferenceVideo(quality.model, preset.mode)
				? getBillableVideoSeconds(video.durationSec)
				: 0,
		);
	};

	// note 上限按每档最长的模板写法算、取最紧的那档，切模板或画质时不会突然超长
	const noteMax = Math.min(
		...preset.qualities.map(
			(quality) =>
				getI2VPromptMaxLength(quality.model, preset.mode) -
				preset.notePrefixLength -
				Math.max(
					...preset.templates.flatMap((item) => [
						getTemplatePrompt(item, quality.id).length,
						getTemplatePrompt(item, quality.id, true).length,
					]),
				),
		),
	);

	// 锁：有独立 pendingStateKey 后正常不会偏，防的是历史行回填（Reprompt / Regenerate）
	// 和旧版模板（prompt 对不上任何一条）。档位闸门把 HD 拉回 480p 时正好落到 Standard
	useEffect(() => {
		if (mode !== preset.mode) {
			setMode(preset.mode);
		} else if (!activeQuality) {
			setModel(defaultQuality.model);
			setResolution(defaultQuality.resolution);
		} else if (
			userVideo &&
			!userVideo.qualityIds.includes(activeQuality.id)
		) {
			// 用户视频模板不开的档（AI Dance 的 Lite）：切到第一个开的档
			const fallback = preset.qualities.find((item) =>
				userVideo.qualityIds.includes(item.id),
			);
			if (fallback) {
				setModel(fallback.model);
				setResolution(fallback.resolution);
				if (fallback.duration) {
					setDuration(fallback.duration);
				}
			}
		} else if (
			activeQuality.duration
				? duration !== activeQuality.duration
				: !preset.durationOptions.includes(duration)
		) {
			setDuration(activeQuality.duration ?? preset.defaultDuration);
		} else if (!activeTemplate && preset.templates[0]) {
			setPrompt(
				getTemplatePrompt(
					preset.templates[0],
					activeQuality.id,
					optionalEmpty,
				),
			);
		} else if (
			activeTemplatePrompt !== undefined &&
			activeMatch?.text !== activeTemplatePrompt
		) {
			// 换了画质档：模板换成这一档的写法，note 原样带过去
			setPrompt(preset.buildPrompt(activeTemplatePrompt, note));
		}
	}, [
		preset,
		mode,
		activeQuality,
		defaultQuality,
		userVideo,
		optionalEmpty,
		activeTemplate,
		activeMatch?.text,
		activeTemplatePrompt,
		note,
		duration,
		setMode,
		setModel,
		setResolution,
		setDuration,
		setPrompt,
	]);

	// 比例：hook 在 Grok 没齐两张图时会改成 auto，图齐了按本地这份写回；
	// 用户点选或恢复现场带进来的合法值同步回本地。模板带参考视频时锁成它的比例，
	// 不写进本地这份，解锁时按本地这份写回
	const wasRatioLocked = useRef(false);
	useEffect(() => {
		if (!canPickAspectRatio) {
			return;
		}
		if (lockedRatio) {
			wasRatioLocked.current = true;
			if (aspectRatio !== lockedRatio) {
				setAspectRatio(lockedRatio);
			}
		} else if (wasRatioLocked.current) {
			wasRatioLocked.current = false;
			setAspectRatio(presetRatio);
		} else if (preset.aspectRatioOptions.includes(aspectRatio)) {
			setPresetRatio(aspectRatio);
		} else {
			setAspectRatio(presetRatio);
		}
	}, [
		preset,
		canPickAspectRatio,
		lockedRatio,
		aspectRatio,
		presetRatio,
		setAspectRatio,
	]);

	// 固定参考视频跟着模板和档位走：该带时表单里只留这一段，不该带时清空；
	// 用户视频模板只清掉别的模板留下的固定视频
	const presetVideoUrl = presetVideo?.url;
	const presetVideoDuration = presetVideo?.durationSec ?? 0;
	const keepUserVideos = Boolean(userVideo);
	useEffect(() => {
		if (presetVideoUrl) {
			if (
				referenceMedia.length !== 1 ||
				referenceMedia[0].url !== presetVideoUrl
			) {
				restoreReferenceMedia([
					{
						kind: "video",
						url: presetVideoUrl,
						durationSec: presetVideoDuration,
					},
				]);
			}
		} else if (keepUserVideos) {
			if (
				referenceMedia.some((item) =>
					presetVideoUrls.has(item.url ?? ""),
				)
			) {
				restoreReferenceMedia(
					referenceMedia.flatMap((item) =>
						item.url && !presetVideoUrls.has(item.url)
							? [
									{
										kind: item.kind,
										url: item.url,
										durationSec: item.durationSec,
									},
								]
							: [],
					),
				);
			}
		} else if (referenceMedia.length) {
			restoreReferenceMedia([]);
		}
	}, [
		presetVideoUrl,
		presetVideoDuration,
		keepUserVideos,
		presetVideoUrls,
		referenceMedia,
		restoreReferenceMedia,
	]);

	// 下面样片区的「Try this example」：按固定槽位填图（只填 previewUrl，生成时原样交给后端）；
	// 样片的图比槽少时把多出来的槽清掉，免得上次留的人跟着一起送给模型
	const slotCount = preset.slotLabels.length;
	useEffect(() => {
		const onSetImages = (event: Event) => {
			const urls = (event as CustomEvent<string[]>).detail;
			urls.slice(0, slotCount).forEach((url, index) => {
				setImageAt(index, { previewUrl: url });
			});
			for (let index = urls.length; index < slotCount; index++) {
				setImageAt(index, null);
			}
		};
		window.addEventListener(VIDEO_HERO_IMAGES_EVENT, onSetImages);
		return () =>
			window.removeEventListener(VIDEO_HERO_IMAGES_EVENT, onSetImages);
	}, [slotCount, setImageAt]);

	// 带默认图的槽：空着时填回默认图（等 mode 先被锁回预设的 reference）
	const slotDefaults = preset.slotDefaults;
	useEffect(() => {
		if (!slotDefaults || mode !== preset.mode) {
			return;
		}
		inputImages.forEach((image, index) => {
			const url = slotDefaults[index];
			if (!image && url) {
				setImageAt(index, { previewUrl: url }, true);
			}
		});
	}, [slotDefaults, preset.mode, mode, inputImages, setImageAt]);

	// 样片的「Try this example」先切模板再填图：note 保留，只换模板部分
	useEffect(() => {
		const onSetTemplate = (event: Event) => {
			const id = (event as CustomEvent<string>).detail;
			const template = preset.templates.find((item) => item.id === id);
			if (template) {
				setPrompt(
					preset.buildPrompt(
						getTemplatePrompt(
							template,
							activeQuality?.id,
							optionalEmpty,
						),
						note,
					),
				);
			}
		};
		window.addEventListener(VIDEO_HERO_TEMPLATE_EVENT, onSetTemplate);
		return () =>
			window.removeEventListener(
				VIDEO_HERO_TEMPLATE_EVENT,
				onSetTemplate,
			);
	}, [preset, note, activeQuality?.id, optionalEmpty, setPrompt]);

	return {
		form,
		activeTemplate,
		activeQuality,
		note,
		setNote,
		noteMax,
		selectQuality,
		selectTemplate,
		qualityCredits,
		isQualityDisabled,
		userVideo,
		userVideos,
		userVideoMissing,
		addUserVideo,
		presetRatio,
		selectRatio,
		lockedRatio,
		presetDurations,
	};
}
